// src/app/shared/navigation/modal-navigation.service.ts
import { Injectable, inject } from '@angular/core';
import { Router, ActivatedRoute, NavigationExtras } from '@angular/router';
import { Location } from '@angular/common';
import { DonationType } from '../types/donation-flow.models';
import { getDonationStepPath } from '../types/donation-routes';

@Injectable({ providedIn: 'root' })
export class ModalNavigationService {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private location = inject(Location);

  // Config mínimos (ajusta aquí si cambian rutas)
  private readonly MODAL_OUTLET = 'modal';
  private readonly MODAL_ROOT = 'donation-modal';
  private readonly CUSTOM_ROOT = 'donation-custom';
  private readonly START_POPUP = `${this.MODAL_ROOT}/pop-up`;
  private readonly START_STEP1 = `${this.MODAL_ROOT}/step/1`;

  private getPrimarySegments(url = this.router.url): string[] {
    const tree = this.router.parseUrl(url);
    const outletGroup = tree.root.children['primary'];
    return outletGroup?.segments?.map((s) => s.path) ?? [];
  }

  private isCustomDonationContext(url = this.router.url): boolean {
    const primary = this.getPrimarySegments(url);
    return primary[0] === this.CUSTOM_ROOT;
  }

  private navigateDonationPath(
    segments: Array<string | number>,
    extras?: NavigationExtras
  ) {
    if (this.isCustomDonationContext()) {
      return this.router.navigate([this.CUSTOM_ROOT, ...segments], extras);
    }

    return this.router.navigate(
      [{ outlets: { [this.MODAL_OUTLET]: [this.MODAL_ROOT, ...segments] } }],
      { relativeTo: this.route.root, ...extras }
    );
  }

  /** Obtiene los segmentos del outlet "modal" para la URL dada (o la actual) */
  getModalSegments(url = this.router.url): string[] {
    const tree = this.router.parseUrl(url);
    const outletGroup = tree.root.children[this.MODAL_OUTLET];
    return outletGroup?.segments?.map((s) => s.path) ?? [];
  }

  /** ¿La URL actual del outlet modal proviene de pop-up? */
  private cameFromPopup(segs?: string[]): boolean {
    const arr = segs ?? this.getModalSegments();
    // Esperado: ['donation-modal', 'pop-up']
    return arr.length >= 2 && arr[0] === this.MODAL_ROOT && arr[1] === 'pop-up';
  }

  /** Decide el “inicio” correcto según la URL actual del outlet modal */
  private startTargetFor(segs?: string[]): string {
    const arr = segs ?? this.getModalSegments();
    if (this.cameFromPopup(arr)) return this.START_POPUP;
    // Si estás en cualquier rama de donation-modal (step/1, one-time/step/2, etc.) → regresa a step/1
    if (arr[0] === this.MODAL_ROOT) return this.START_STEP1;
    // Si no hay outlet modal en la URL, usa step/1 como default
    return this.START_STEP1;
  }

  /** “Regresar” inteligente: si hay historial, usa back(); si no, deduce y navega al inicio correcto */
  smartBack(extras?: NavigationExtras): void {
    if (window.history.length > 1) {
      this.location.back();
    } else {
      this.navigateToModalStart({ replaceUrl: true, ...extras });
    }
  }

  /** Ir al “inicio” correcto del modal (pop-up o step/1, según la URL actual) */
  navigateToModalStart(extras?: NavigationExtras) {
    if (this.isCustomDonationContext()) {
      return this.router.navigate([this.CUSTOM_ROOT, 'step', '1'], extras);
    }

    const target = this.startTargetFor();
    return this.router.navigate(
      [{ outlets: { [this.MODAL_OUTLET]: target } }],
      { relativeTo: this.route.root, ...extras }
    );
  }

  /** Abrir pop-up explícitamente */
  openPopUp(extras?: NavigationExtras) {
    return this.navigateDonationPath(['pop-up'], extras);
  }

  /** Abrir step/1 explícitamente */
  openStep1(extras?: NavigationExtras) {
    return this.navigateDonationPath(['step', 1], extras);
  }

  navigateToPaymentStep(
    donationType: DonationType,
    step: number,
    extras?: NavigationExtras
  ) {
    return this.navigateDonationPath(getDonationStepPath(donationType, step), extras);
  }

  /** Cerrar el outlet modal */
  closeModal(extras?: NavigationExtras) {
    if (this.isCustomDonationContext()) {
      return this.router.navigate(['/'], extras);
    }

    return this.router.navigate([{ outlets: { [this.MODAL_OUTLET]: null } }], {
      relativeTo: this.route.root,
      ...extras,
    });
  }
}
